/**
 * The shell origin's binding of `@zion/surface-host` (kernel apps, WS-16d, change group G4 of
 * `design-docs/WS-16.md`; WS-14 section 3.5 and 3.6; feature 2026-09-18-audience-bound-dp-token
 * D-1, D-3, R-055; layered shell surfaces R-127).
 *
 * `@zion/surface-host` holds the services every surface mount gets, and its only origin-specific
 * input is one `OriginFetchPolicy`. This module is the shell's policy and the one place that
 * builds the provider on `<dp>`:
 *
 * - {@link createShellFetchPolicy} builds the policy over the shell's API client
 *   (`sharedAuthFetch`, `createAppFetch`) and the reference-counted app-token ledger that moved here
 *   from the deleted `services/authed-fetch-service.ts`.
 * - {@link createShellServices} builds `createSurfaceServices` over that policy and ends the
 *   policy with the provider.
 * - {@link createShellExtensionPointSource} builds the `ExtensionPointSource` that the provider's
 *   extension point board reads: the shell registry and the installed loader host (surface
 *   extension points, D10, WS-7).
 *
 * The kernel origin builds its own policy over the kernel token (WS-15). No credential of one
 * origin enters the other's policy.
 */
import { extractClaims, getAccessToken, mintAppToken } from '@zion/sdk-react/auth-core';
import type { SurfaceDeclaration } from '@zion/sdk-contracts';
import type { ExtensionImplementer, SurfaceHost } from '@zion/sdk-surfaces/v1';
import {
  createRootShellSettingsSource,
  createSurfaceServices,
  type BearerIdentity,
  type ExtensionPointSource,
  type HistorySource,
  type OriginFetchPolicy,
  type ServiceClock,
  type ServiceSession,
  type SurfaceServicesHost,
} from '@zion/surface-host';
import { createAppFetch, sharedAuthFetch } from '../api/client.js';
import type { AppRegistryEntry } from '../api/types.js';
import {
  clearAppTokens,
  ensureAppToken,
  evictAppToken,
  syncSessionToken,
  type MintedAppToken,
} from '../auth/app-token-store.js';

/**
 * The shell origin's `OriginFetchPolicy`, plus its end.
 *
 * - `platformFetch` is `sharedAuthFetch`: the live session bearer, the 401 refresh, the cold-start
 *   retry, the maintenance overlay and the per-attempt header deadline that `initApiClient` set
 *   (`SHELL_ATTEMPT_TIMEOUT_MS` in production). A `/v1/admin` target with the kernel origin on
 *   answers the local 403 `kernel_origin_required` and sends nothing (WS-16b).
 * - `appFetch(appUuid, attemptTimeoutMs)` is `createAppFetch(appUuid, attemptTimeoutMs, isRetained)`,
 *   where `isRetained(uuid)` answers whether the retain count of `uuid` is above 0. It gives the
 *   session bearer on a `'reserved'` target, the app's own token on an `'app'` target, no bearer on
 *   a `'cross-origin'` target, and the local 403 on a `'kernel-only'` target while the kernel
 *   origin is on. With `attemptTimeoutMs`, each network attempt waits at most that long for its
 *   headers and each retry has a fresh deadline; without it, no attempt has a deadline. A value
 *   that is not a finite number above 0 and at most 2147483647 throws `RangeError` at the call.
 *   For an app with no live mount, a 401 or a retry mints no token, and a send that waited for
 *   the mint rejects with `disposedError('fetch')` and sends nothing.
 * - `retainApp(appUuid)` counts one more live mount of the app and answers its idempotent release.
 *   `retainApp(null)` counts nothing and answers a no-op. The first retain of a uuid, with a session
 *   token present, mints through `POST /v1/auth/app-token` into the shell's app-token store; a
 *   failed mint stores nothing and rejects into nothing. The last release evicts the token. A mint
 *   that answers after that release stores its token and the policy evicts it at once, so no app
 *   bearer outlives the last live mount. On a session token change the policy calls
 *   `syncSessionToken(token)` first, which empties the store, then mints again for every retained
 *   uuid when the new token is non-null. A change of the claims alone mints nothing.
 * - `liveIdentity()` answers the `sub` and `orgId` claims of the session bearer that
 *   `platformFetch` attaches now (`getAccessToken()`), each `null` when the claim is absent or the
 *   token does not decode, and `null` when no session bearer is stored. The decode checks no
 *   signature: the answer serves the identity comparison of the preferences store only.
 * - `dispose()` stops the session subscription, forgets every count and calls `clearAppTokens`. A
 *   release after it changes nothing. A second call is a no-op.
 */
export interface ShellFetchPolicy extends OriginFetchPolicy {
  dispose(): void;
}

/** One exchange for one app, shaped for `ensureAppToken`: the bearer and the server's lifetime (R-041). */
function mintFor(appUuid: string): Promise<MintedAppToken> {
  return mintAppToken(appUuid, sharedAuthFetch).then((response) => ({
    accessToken: response.accessToken,
    expiresInSec: response.expiresIn,
  }));
}

/**
 * Build the shell origin's policy over the session port. It records the current session token in
 * the app-token store at once (`syncSessionToken`), and listens to the session until `dispose`.
 *
 * @param session The loader's session port (`createServiceSession` in `session.ts`).
 */
export function createShellFetchPolicy(session: ServiceSession): ShellFetchPolicy {
  const counts = new Map<string, number>();
  let token = session.current().token;
  let disposed = false;
  syncSessionToken(token);

  // A mount can end before its mint answers. The store then receives the token after the last
  // release has evicted nothing, so the settled mint checks the count again and evicts the token
  // when no live mount of that app is left.
  const mint = (appUuid: string): void => {
    void ensureAppToken(appUuid, () => mintFor(appUuid)).then(() => {
      if (!counts.has(appUuid)) evictAppToken(appUuid);
    });
  };

  const stopSession = session.subscribe(() => {
    const next = session.current().token;
    if (next === token) return;
    token = next;
    syncSessionToken(next);
    if (next === null) return;
    for (const appUuid of counts.keys()) mint(appUuid);
  });

  return {
    platformFetch: sharedAuthFetch,
    // A send whose mount ended mints no token and, after a wait for the mint, sends nothing.
    appFetch: (appUuid, attemptTimeoutMs) => createAppFetch(appUuid, attemptTimeoutMs, (uuid) => counts.has(uuid)),
    retainApp(appUuid) {
      if (appUuid === null || disposed) return () => undefined;
      const count = counts.get(appUuid) ?? 0;
      counts.set(appUuid, count + 1);
      if (count === 0 && token !== null) mint(appUuid);
      let released = false;
      return () => {
        if (released) return;
        released = true;
        const left = (counts.get(appUuid) ?? 1) - 1;
        if (left > 0) {
          counts.set(appUuid, left);
          return;
        }
        counts.delete(appUuid);
        evictAppToken(appUuid);
      };
    },
    liveIdentity(): BearerIdentity | null {
      const bearer = getAccessToken();
      if (bearer === null) return null;
      const claims = extractClaims(bearer);
      return { sub: claims.sub, orgId: claims.orgId };
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      stopSession();
      counts.clear();
      clearAppTokens();
    },
  };
}

/**
 * What the boot hands {@link createShellServices}.
 *
 * - `session`: the loader's session port.
 * - `history`: the boot's one navigation watcher.
 * - `overlay`: the connected `#zion-overlay` element.
 * - `window`: the document's window. The provider reads its `matchMedia` and its live `location`.
 * - `clock`: `systemClock` in production.
 * - `extensionPoints`: the shell's {@link createShellExtensionPointSource}, which the provider's
 *   extension point board reads for `services.extensions` and `services.slots` (surface extension
 *   points, WS-7, R-011). The shell always has one; only the kernel SPA passes none (D16).
 */
export interface ShellServicesDeps {
  readonly session: ServiceSession;
  readonly history: HistorySource;
  readonly overlay: HTMLElement;
  readonly window: Window;
  readonly clock: ServiceClock;
  readonly extensionPoints: ExtensionPointSource;
}

/**
 * What {@link createShellExtensionPointSource} reads. Every member is read at each call, so a
 * later registry or a host installed after the source shows.
 *
 * - `registry`: the principal's registry, `appRegistrySnapshot` in production.
 * - `registryChanges`: a subscription to registry changes, `subscribeAppRegistry` in production.
 *   It answers an unsubscribe.
 * - `session`: the loader's session port, for the permission change.
 * - `host`: the installed surface host, or `null` before the install. The boot installs the host
 *   after it builds the provider, so the source reads it lazily. In the kernel `on` arm the
 *   installed host is the kernel-guarded one, so the guard holds here too.
 */
export interface ShellExtensionPointSourceInputs {
  readonly registry: () => readonly AppRegistryEntry[];
  readonly registryChanges: (listener: () => void) => () => void;
  readonly session: ServiceSession;
  readonly host: () => Pick<SurfaceHost, 'implementers'> | null;
}

/** A permission set as one comparable string: order and repeats do not count. Pure. */
function permissionKeyOf(session: ServiceSession): string {
  return JSON.stringify(Array.from(new Set(session.current().orgPermissions)).sort());
}

/**
 * The shell's `ExtensionPointSource` (surface extension points, D9, D10, WS-7; WS-6 E6-A). The
 * caller is the boot, which hands it to {@link createShellServices}; the provider's extension
 * point board is the one reader.
 *
 * - `implementers(point)`: the installed host's `implementers(point)`, so `has` and `list` pass the
 *   same checks as a mount: permission, bundle, same origin and, in the kernel `on` arm, the kernel
 *   guard. `[]` while no host is installed. A throw of the host propagates; the board reads it as
 *   `[]`. The board copies, de-duplicates and sorts the answer itself.
 * - `declarationOf(appId, surface)`: the first declaration named `surface` on the first registry
 *   row of `appId`, as `resolve` reads them, or `null` for an unknown app or surface. The system
 *   app has no registry row, so its mounts list no point.
 * - `subscribe(listener)`: `listener` runs after each registry change, and after each session
 *   change that changes the set of `orgPermissions`, so an implementer that a permission hides or
 *   shows is heard. A session change that keeps the permission set (a token refresh) runs nothing.
 *   A throw of `listener` propagates to the writer's guard. Returns an idempotent unsubscribe that
 *   ends both subscriptions.
 *
 * No side effect at build: the source subscribes to nothing until its `subscribe` runs.
 */
export function createShellExtensionPointSource(inputs: ShellExtensionPointSourceInputs): ExtensionPointSource {
  return {
    implementers(point): readonly ExtensionImplementer[] {
      return inputs.host()?.implementers(point) ?? Object.freeze([]);
    },
    declarationOf(appId, surface): SurfaceDeclaration | null {
      const row = inputs.registry().find((entry) => entry.appId === appId);
      return row?.surfaces.find((decl) => decl.name === surface) ?? null;
    },
    subscribe(listener) {
      let permissions = permissionKeyOf(inputs.session);
      const stopRegistry = inputs.registryChanges(() => listener());
      const stopSession = inputs.session.subscribe(() => {
        const next = permissionKeyOf(inputs.session);
        if (next === permissions) return;
        permissions = next;
        listener();
      });
      let stopped = false;
      return () => {
        if (stopped) return;
        stopped = true;
        stopRegistry();
        stopSession();
      };
    },
  };
}

/**
 * Build the shell's one `SurfaceServicesProvider`: `createSurfaceServices` of `@zion/surface-host`
 * over {@link createShellFetchPolicy}, with the org's root-shell settings read through the policy's
 * `platformFetch` (`createRootShellSettingsSource`, R-60), and with `deps.extensionPoints` as the
 * source of each mount's `extensions` and `slots` (surface extension points, WS-7, R-011). The
 * provider subscribes to that source once at build and unsubscribes at `dispose`.
 *
 * @returns The provider. Its `dispose()` ends the provider, then the policy, so the app-token store
 *   is empty after it, as the deleted `createLoaderServices` left it. A second call is a no-op.
 * @throws `TypeError` from `createSurfaceServices` for an overlay other than a connected
 *   `#zion-overlay`. The policy is ended before the throw.
 */
export function createShellServices(deps: ShellServicesDeps): SurfaceServicesHost {
  const policy = createShellFetchPolicy(deps.session);
  let host: SurfaceServicesHost;
  try {
    host = createSurfaceServices({
      session: deps.session,
      clock: deps.clock,
      matchMedia: (query) => deps.window.matchMedia(query),
      history: deps.history,
      overlay: deps.overlay,
      location: deps.window.location,
      policy,
      shellSettings: createRootShellSettingsSource(policy.platformFetch),
      extensionPoints: deps.extensionPoints,
    });
  } catch (err) {
    policy.dispose();
    throw err;
  }
  return {
    servicesFor: (request) => host.servicesFor(request),
    releaseServices: (mountId) => host.releaseServices(mountId),
    updatePermissionKeys: (mountId, keys) => host.updatePermissionKeys(mountId, keys),
    rootServices: (mountId) => host.rootServices(mountId),
    dispose() {
      host.dispose();
      policy.dispose();
    },
  };
}
